// ─── Fingerprint scanner integration ─────────────────────────────────────────
// The counter's fingerprint reader (Mantra, Morpho/Idemia, SecuGen, Startek,
// Evolute …) ships a Windows "RD Service" that exposes a small HTTP server on
// localhost. The browser talks to it directly — no backend hop:
//
//   RDSERVICE  http://127.0.0.1:<port>        → is a device plugged in & ready?
//   CAPTURE    http://127.0.0.1:<port>/rd/capture → scan one finger, return PID XML
//
// Vendors don't agree on a port, so the ready device is found by probing the
// standard 11100-11120 window once and remembering the winner for the rest of
// the session.
//
// The captured PID XML block is stored VERBATIM (base64-encoded on the wire) in
// pawn_broking.fingerprint_1 / fingerprint_2. It is the vendor-neutral record
// of the scan and carries the template, its format and the device serial, so a
// later verification can be done against whatever reader the store owns.
//
// No reader / no RD service is NOT an error: the operator can attach a template
// file their reader's own software produced, or skip the finger entirely — the
// column then stays NULL.

// Port window to probe, overridable via REACT_APP_FINGERPRINT_RD_PORTS
// ("11100-11120", or a comma-separated list).
const parsePorts = (spec) => {
  const raw = String(spec || "").trim();
  if (!raw) return null;
  if (raw.includes("-")) {
    const [from, to] = raw.split("-").map((n) => parseInt(n, 10));
    if (!isNaN(from) && !isNaN(to) && to >= from) {
      const out = [];
      for (let p = from; p <= to; p++) out.push(p);
      return out;
    }
    return null;
  }
  const list = raw
    .split(",")
    .map((n) => parseInt(n.trim(), 10))
    .filter((n) => !isNaN(n));
  return list.length ? list : null;
};

const DEFAULT_PORTS = (() => {
  const out = [];
  for (let p = 11100; p <= 11120; p++) out.push(p);
  return out;
})();

export const RD_PORTS =
  parsePorts(process.env.REACT_APP_FINGERPRINT_RD_PORTS) || DEFAULT_PORTS;

// A single probe is given a short leash — a port with nothing behind it must
// not hold the whole discovery hostage.
const PROBE_TIMEOUT_MS = 1200;
// The capture itself waits on a human placing a finger, so it gets much longer.
const CAPTURE_TIMEOUT_MS = 25000;

// Port that answered last time, so the second finger doesn't re-probe.
let cachedPort = null;

const fetchWithTimeout = async (url, options, timeoutMs) => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(url, { ...options, signal: controller.signal });
  } finally {
    clearTimeout(timer);
  }
};

// UTF-8 safe string → base64 (btoa alone throws on non-Latin1 characters, and
// PID XML can carry a device model name with any character in it).
export const toBase64 = (text) => {
  const bytes = new TextEncoder().encode(String(text));
  let binary = "";
  bytes.forEach((b) => {
    binary += String.fromCharCode(b);
  });
  return btoa(binary);
};

// Pull an attribute out of a tag in the RD service's XML reply. Deliberately
// regex-based rather than DOMParser: the replies are tiny, flat and often not
// well-formed enough for a strict parser.
const attrOf = (xml, tag, attr) => {
  const m = new RegExp(`<${tag}\\b[^>]*\\b${attr}\\s*=\\s*"([^"]*)"`, "i").exec(
    xml || "",
  );
  return m ? m[1] : null;
};

// Probe one port. Resolves to { port, info } when a READY device answers,
// otherwise null — a closed port, a timeout and a busy device all look the same
// to us and simply mean "not this one".
const probePort = async (port) => {
  try {
    const res = await fetchWithTimeout(
      `http://127.0.0.1:${port}`,
      { method: "RDSERVICE" },
      PROBE_TIMEOUT_MS,
    );
    if (!res.ok) return null;
    const xml = await res.text();
    if (!/RDService/i.test(xml)) return null;
    const status = attrOf(xml, "RDService", "status");
    if (status && status.toUpperCase() !== "READY") return null;
    return {
      port,
      info: attrOf(xml, "RDService", "info") || `Device on port ${port}`,
      // The CAPTURE path is advertised by the service; fall back to the
      // near-universal /rd/capture when it isn't.
      capturePath: /path\s*=\s*"([^"]*capture[^"]*)"/i.exec(xml)?.[1] || "/rd/capture",
    };
  } catch {
    return null;
  }
};

// Find a ready reader. All ports are probed at once (they are local and cheap)
// and the lowest-numbered ready one wins, so the result is stable across runs.
export const discoverScanner = async ({ refresh = false } = {}) => {
  if (cachedPort && !refresh) {
    const again = await probePort(cachedPort);
    if (again) return again;
    cachedPort = null;
  }
  const results = await Promise.all(RD_PORTS.map(probePort));
  const found = results.find(Boolean) || null;
  cachedPort = found ? found.port : null;
  return found;
};

// PidOptions asking for exactly one finger, XML response, no Aadhaar wadh
// (this is a local record, not an authentication call).
const pidOptions = (timeoutMs) =>
  `<?xml version="1.0"?>` +
  `<PidOptions ver="1.0">` +
  `<Opts fCount="1" fType="2" iCount="0" pCount="0" format="0" ` +
  `pidVer="2.0" timeout="${timeoutMs}" posh="UNKNOWN" env="P" />` +
  `</PidOptions>`;

// Scan one finger. Returns { data, quality, device, format } where `data` is
// the base64 the backend stores as the BLOB. Throws with a human-readable
// message when no device is present or the scan fails/was cancelled.
export const captureFingerprint = async ({
  timeoutMs = CAPTURE_TIMEOUT_MS,
} = {}) => {
  const device = await discoverScanner();
  if (!device) {
    throw new Error(
      "No fingerprint scanner found. Make sure the reader is plugged in and its RD Service is running.",
    );
  }
  const res = await fetchWithTimeout(
    `http://127.0.0.1:${device.port}${device.capturePath}`,
    {
      method: "CAPTURE",
      headers: { "Content-Type": "text/xml; charset=UTF-8", Accept: "text/xml" },
      body: pidOptions(timeoutMs),
    },
    timeoutMs + 5000,
  );
  const xml = await res.text();
  if (!res.ok) throw new Error(`Scanner returned HTTP ${res.status}`);

  // errCode 0 is the only success. Everything else (finger not placed, device
  // timeout, service busy) carries its own message in errInfo.
  const errCode = attrOf(xml, "Resp", "errCode");
  if (errCode !== null && errCode !== "0") {
    throw new Error(
      attrOf(xml, "Resp", "errInfo") || `Scan failed (code ${errCode})`,
    );
  }
  if (!/PidData/i.test(xml)) throw new Error("Scanner returned no fingerprint data");

  const quality = parseInt(attrOf(xml, "Resp", "qScore"), 10);
  return {
    data: toBase64(xml),
    quality: isNaN(quality) ? null : quality,
    device: device.info,
    source: "scanner",
    format: "PID-XML",
  };
};

// Fallback path: attach a template/image file the reader's own software wrote
// (.iso, .ansi, .fmr, .bmp, .png …). Stored byte-for-byte, same as a scan.
export const fingerprintFromFile = (file) =>
  new Promise((resolve, reject) => {
    if (!file) {
      reject(new Error("No file selected"));
      return;
    }
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Could not read the file"));
    reader.onload = () => {
      const result = String(reader.result || "");
      const comma = result.indexOf(",");
      const data = comma >= 0 ? result.slice(comma + 1) : result;
      if (!data) {
        reject(new Error("The selected file is empty"));
        return;
      }
      resolve({
        data,
        quality: null,
        device: file.name,
        source: "file",
        format: file.name.split(".").pop()?.toUpperCase() || "FILE",
      });
    };
    reader.readAsDataURL(file);
  });

// Approximate on-screen size of a stored scan, from its base64 length.
export const fingerprintSizeLabel = (data) => {
  if (!data) return "";
  const bytes = Math.floor((String(data).length * 3) / 4);
  return bytes >= 1024 ? `${(bytes / 1024).toFixed(1)} KB` : `${bytes} B`;
};
